import React from 'react';
import { Card, EmptyState, MaterialIcon } from '../../ui';
import { getDocumentEngagement } from '../../../services/activityService';
import { formatExact, formatRelative, plural } from './activityFormat';

const DAYS = 90;

/** Top 10 most-opened documents over the last 90 days. Honours "Show admin & test". */
const TopDocumentsCard = ({ hoaId, includeAdmin, refreshKey }) => {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!hoaId) return undefined;
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const data = await getDocumentEngagement({ hoaId, days: DAYS, includeExcluded: includeAdmin, limit: 10 });
        if (!active) return;
        setRows(data);
        setError('');
      } catch (err) {
        if (!active) return;
        setRows([]);
        setError(err?.message || 'Document activity could not be loaded.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [hoaId, includeAdmin, refreshKey]);

  return (
    <Card>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center">
          <MaterialIcon name="description" size={20} className="text-secondary" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-primary-dark">Most-opened documents</h3>
          <p className="text-xs font-medium text-slate-500">Last {DAYS} days</p>
        </div>
      </div>

      {loading ? (
        <p className="py-8 text-center text-sm text-slate-500">Loading…</p>
      ) : error ? (
        <p className="py-8 text-center text-sm text-red-700">{error}</p>
      ) : rows.length === 0 ? (
        <EmptyState icon="description" title="No documents opened yet by members" />
      ) : (
        <ol className="space-y-2">
          {rows.map((d, i) => (
            <li
              key={d.document_id || `row-${i}`}
              className="flex items-start gap-3 p-3 rounded-2xl bg-main-bg/60 border border-amber-100/40"
            >
              <span className="w-7 h-7 rounded-full bg-white text-xs font-extrabold text-secondary flex items-center justify-center shrink-0">
                {i + 1}
              </span>
              <span className="min-w-0 flex-1">
                <span className={`block text-sm font-bold break-words ${d.still_exists ? 'text-primary-dark' : 'text-slate-400'}`}>
                  {d.title}
                  {!d.still_exists && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider">(removed)</span>}
                </span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  {plural(d.views, 'view')} · {plural(d.unique_viewers, 'reader')} ·{' '}
                  <span title={formatExact(d.last_viewed_at)}>last opened {formatRelative(d.last_viewed_at)}</span>
                </span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
};

export default TopDocumentsCard;
